/* eslint no-underscore-dangle: 0 */
// Vue.js Memory Leak Fix
// This patch fixes the issue where DOM event listeners are not properly removed
// when vnodes are destroyed. Original fix: https://github.com/vuejs/vue/pull/10085

import Vue from 'vue';

/**
 * Finds the updateDOMListeners module from Vue's internal modules.
 * @returns {Object|null} The updateDOMListeners module or null if not found.
 */
const findUpdateDOMListenersModule = () => {
  const { modules } = Vue.options._base.options;
  if (!modules) return null;
  return modules.find(
    module => module.create === module.update && module.create.name === 'updateDOMListeners',
  );
};

/**
 * Patched version of updateDOMListeners to track and manage event listeners.
 * @param {VNode} oldVnode - The previous virtual node.
 * @param {VNode} vnode - The current virtual node.
 */
function patchedUpdateDOMListeners(oldVnode, vnode) {
  if (!oldVnode.data && !vnode.data) return;

  const on = vnode.data.on || {};
  const oldOn = oldVnode.data.on || {};
  const target = vnode.elm || oldVnode.elm;

  if (!target) return;

  const add = (name, handler, capture) => {
    if (!target._vueEventListeners) {
      target._vueEventListeners = new Map();
    }
    target._vueEventListeners.set(name, handler);
    target.addEventListener(name, handler, capture);
  };

  const remove = (name, handler, capture) => {
    if (target._vueEventListeners) {
      target._vueEventListeners.delete(name);
    }
    target.removeEventListener(name, handler, capture);
  };

  const createOnceHandler = (name, handler, capture) => function onceHandler(...args) {
    const res = handler(...args);
    if (res !== null) {
      remove(name, onceHandler, capture);
    }
  };

  Object.keys(on).forEach(name => {
    const handler = on[name];
    const oldHandler = oldOn[name];

    if (!oldHandler) {
      if (handler.once) {
        add(name, createOnceHandler(name, handler, false), false);
      } else {
        add(name, handler, false);
      }
    } else if (handler !== oldHandler) {
      remove(name, oldHandler, false);
      if (handler.once) {
        add(name, createOnceHandler(name, handler, false), false);
      } else {
        add(name, handler, false);
      }
    }
  });

  Object.keys(oldOn).forEach(name => {
    if (!on[name]) {
      remove(name, oldOn[name], false);
    }
  });
}

/**
 * Recursively cleans up all tracked event listeners from an element and its children.
 * @param {Element} element - The DOM element to clean up.
 */
function cleanupEventListeners(element) {
  if (!element) return;
  if (element._vueEventListeners) {
    element._vueEventListeners.forEach((handler, name) => {
      element.removeEventListener(name, handler, false);
    });
    element._vueEventListeners.clear();
  }
  if (element.children) {
    Array.from(element.children).forEach(child => cleanupEventListeners(child));
  }
}

const module = findUpdateDOMListenersModule();
if (module) {
  const originalCreate = module.create;
  const originalUpdate = module.update;

  module.create = (oldVnode, vnode) => {
    if (oldVnode && oldVnode.elm) {
      cleanupEventListeners(oldVnode.elm);
    }
    originalCreate(oldVnode, vnode);
    patchedUpdateDOMListeners(oldVnode, vnode);
  };

  module.update = (oldVnode, vnode) => {
    if (oldVnode && oldVnode.elm) {
      cleanupEventListeners(oldVnode.elm);
    }
    originalUpdate(oldVnode, vnode);
    patchedUpdateDOMListeners(oldVnode, vnode);
  };

  module.destroy = vnode => {
    if (!vnode || !vnode.elm) return;
    cleanupEventListeners(vnode.elm);
  };
}

/**
 * Monkey-patches addEventListener to track event listeners on elements.
 * @param {string} type - The event type.
 * @param {Function} listener - The event handler.
 * @param {Object|boolean} options - The event options.
 */
const originalAddEventListener = Element.prototype.addEventListener;
Element.prototype.addEventListener = function addEventListener(type, listener, options) {
  if (!this._vueEventListeners) {
    this._vueEventListeners = new Map();
  }
  this._vueEventListeners.set(type, listener);
  return originalAddEventListener.call(this, type, listener, options);
};

/**
 * Monkey-patches removeEventListener to update tracked event listeners on elements.
 * @param {string} type - The event type.
 * @param {Function} listener - The event handler.
 * @param {Object|boolean} options - The event options.
 */
const originalRemoveEventListener = Element.prototype.removeEventListener;
Element.prototype.removeEventListener = function removeEventListener(type, listener, options) {
  if (this._vueEventListeners) {
    this._vueEventListeners.delete(type);
  }
  return originalRemoveEventListener.call(this, type, listener, options);
};

// Add global mixin to ensure cleanup on component destruction
Vue.mixin({
  beforeDestroy() {
    if (this.$el) {
      cleanupEventListeners(this.$el);
    }
  },
  destroyed() {
    if (this.$el) {
      cleanupEventListeners(this.$el);
    }
  },
});

export default Vue;
